import {
  Stack,
  Box,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useDashboardContext } from "../../contexts/DashboardContext";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const TrialPicker = ({
  selectedDate,
  setSelectedDate,
  setCurrentTrialId,
  currentTrialId
}) => {
  const { allTrials } = useDashboardContext();
  const [selectedTrialIndex, setSelectedTrialIndex] = useState("");

  // Compare dates in the local timezone
  const selectedDay = selectedDate ? dayjs(selectedDate).startOf('day') : null;

  const availableTrialsOnSelectedDate = selectedDay
    ? allTrials.filter((trial) => {
        // Treat trial.date as a local date
        const trialDay = dayjs(trial.date).startOf('day');
        return selectedDay.isSame(trialDay);
      })
    : [];

  const shouldDisableDate = (date) => {
    const localDate = dayjs(date).startOf('day');
    return !allTrials.some((trial) => {
      // Treat trial.date as a local date
      const trialDate = dayjs(trial.date).startOf('day');
      return trialDate.isSame(localDate);
    });
  };

  // Handle the selection change in the Select component
  const handleTrialSelect = (event) => {
    const selectedIndex = event.target.value;
    setSelectedTrialIndex(selectedIndex); // Update local state for the Select component

    // Ensure selectedIndex is a valid number and within bounds
    if (typeof selectedIndex === 'number' && selectedIndex >= 0 && selectedIndex < availableTrialsOnSelectedDate.length) {
      const selectedTrial = availableTrialsOnSelectedDate[selectedIndex];
      if (selectedTrial) { // Check if selectedTrial is found
        setCurrentTrialId(selectedTrial.id); // Inform parent
      }
    }
  };

  useEffect(() => {
    if (availableTrialsOnSelectedDate.length > 0) {
      let trialIdToMatch = currentTrialId;
      let indexInList = -1;

      if (trialIdToMatch) {
        indexInList = availableTrialsOnSelectedDate.findIndex(
          trial => String(trial.id) === String(trialIdToMatch)
        );
      }

      if (indexInList !== -1) { // Case 1: currentTrialId is provided and found
        setSelectedTrialIndex(indexInList);
      } else { // Case 2: currentTrialId not provided, or not found in current list
               // Default to first trial in the new list
        const defaultIndex = 0;
        const defaultTrialId = availableTrialsOnSelectedDate[defaultIndex].id;
        setSelectedTrialIndex(defaultIndex);
        // Only update parent if the currentTrialId is actually different or was null
        if (currentTrialId === null || String(currentTrialId) !== String(defaultTrialId)) {
           setCurrentTrialId(defaultTrialId);
        }
      }
    } else { // Case 3: No trials available for this date
      setSelectedTrialIndex("");
      if (currentTrialId !== null) { // If there was a trial selected, clear it
        setCurrentTrialId(null);
      }
    }
  }, [availableTrialsOnSelectedDate, currentTrialId, setCurrentTrialId]);

  return (
    <Box>
      <Stack direction="column" spacing={1}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          {/* Date Picker */}
          <DatePicker
            label="Select Date"
            value={selectedDate}
            onChange={(newValue) => {
              setSelectedDate(newValue);
            }}
            shouldDisableDate={shouldDisableDate}
            sx={{ maxWidth: 200 }}
          />
        </LocalizationProvider>
        {/* Trial Picker */}
        <Box sx={{ maxWidth: 200 }}>
          <FormControl fullWidth variant="outlined">
            {/* <InputLabel id="select-trial-label">Select Trial</InputLabel> */}
            <Select
              id="select-trial"
              labelId="select-trial-label"
              onChange={handleTrialSelect}
              value={selectedTrialIndex}
            >
              {availableTrialsOnSelectedDate.map((trial, index) => (
                <MenuItem divider={true} key={index} value={index}>
                  {trial.startTimestamp}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Stack>
    </Box>
  );
};

export default TrialPicker;
